import { PROBLEM_TYPE_PREFIX } from "@planerius/contracts"
import type { DomainErrorCode } from "@planerius/contracts"
import type { CabinetWarning } from "@planerius/contracts/cabinet"

/**
 * Разбор отказов API в доменную ошибку.
 *
 * ЗАЧЕМ ЭТОТ ФАЙЛ. Сервер отвечает по RFC 9457: машинный код лежит в поле `type`
 * (ссылка вида https://planerius.ru/problems/slot-taken), человеческий заголовок — в `title`.
 * Если кабинет этого не разбирает, любой отказ превращается в «сетевую ошибку»,
 * и администратор за стойкой не понимает разницы между «слот только что заняли»
 * (обновить сетку и предложить соседнее окно) и «клиент в чёрном списке» (позвонить).
 * Поэтому здесь из ответа достаётся КОД, а не текст, — текст переводится и переписывается,
 * код стабилен и документирован.
 */

/**
 * Коды контура входа. Повторяют apps/api/src/modules/auth/auth.errors.ts — в пакете
 * контрактов их нет, поэтому список продублирован здесь и обязан меняться вместе с ним.
 */
export type AuthErrorCode =
  | "invalid_credentials"
  | "mfa_required"
  | "mfa_invalid"
  | "mfa_not_enrolled"
  | "session_invalid"
  | "session_reuse_detected"
  | "session_refresh_raced"
  | "tenant_forbidden"
  | "tenant_not_selected"
  | "tenant_suspended"
  | "permission_denied"
  | "branch_forbidden"
  | "two_factor_stale"
  | "invitation_invalid"
  | "invitation_expired"
  | "invitation_already_accepted"
  | "invitation_sign_in_required"
  | "invitation_email_required"
  | "invitation_wrong_account"
  | "invitation_already_member"
  | "member_status_conflict"
  | "employee_not_found"
  | "employee_bound_elsewhere"
  | "password_weak"
  | "password_reuse"
  | "rate_limited"
  | "version_conflict"
  | "impersonation_forbidden"
  | "impersonation_read_only"

/** Отказы кабинета из CabinetErrorFilter. */
export type CabinetErrorCode = "confirmation_required" | "version_conflict" | "busy_conflicts"

/**
 * Отказы, у которых нет тела problem details: обрыв связи, таймаут, пятисотка от nginx.
 * Их приходится называть самим, иначе интерфейсу нечего показать человеку.
 */
export type TransportErrorCode =
  | "offline"
  | "timeout"
  | "aborted"
  | "server_error"
  | "bad_response"
  | "unknown"

export type KnownApiErrorCode =
  | AuthErrorCode
  | CabinetErrorCode
  | DomainErrorCode
  | TransportErrorCode

/**
 * Набор нарочно открытый: сервер вправе завести новый код вместе с новым разделом,
 * и старый кабинет обязан пережить незнакомый код молча, а не упасть на разборе.
 */
export type ApiErrorCode = KnownApiErrorCode | (string & Record<never, never>)

/** Конфликт занятости из отказа busy_conflicts: что именно мешает занять время. */
export interface BusyConflictInfo {
  readonly kind: "appointment" | "busy" | "hold"
  readonly id: string
  readonly startAt: string
  readonly endAt: string
  readonly employeeId: string | null
  readonly resourceId: string | null
  readonly clientName: string | null
  readonly note: string | null
}

/** Заголовки для отказов без тела. Всё остальное приходит от сервера полем `title`. */
const TRANSPORT_TITLES: Readonly<Record<TransportErrorCode, string>> = {
  offline: "Нет связи",
  timeout: "Сервис не ответил",
  aborted: "Запрос отменён",
  server_error: "Сервис недоступен",
  bad_response: "Не удалось разобрать ответ",
  unknown: "Не удалось получить данные",
}

const TRANSPORT_DETAILS: Readonly<Record<TransportErrorCode, string | null>> = {
  offline: "Показанные данные могут быть неактуальными, изменения сейчас не сохраняются.",
  timeout: "Сервис не ответил за 10 секунд. Данные на экране могут быть неактуальными.",
  aborted: null,
  server_error: "Мы уже знаем о сбое. Повторите через минуту.",
  bad_response: "Ответ сервиса не похож на ожидаемый. Сообщите в поддержку.",
  unknown: null,
}

/**
 * Отказ API. Наследник Error, чтобы падать привычным способом, но с машинным кодом:
 * ветвление в интерфейсе идёт по `code`, а не по разбору русского текста.
 */
export class ApiProblem extends Error {
  readonly code: ApiErrorCode
  readonly status: number
  readonly title: string
  readonly detail: string | null
  readonly type: string
  readonly instance: string | null
  /** Предупреждения из confirmation_required: их показывают словами и подтверждают осознанно. */
  readonly warnings: readonly CabinetWarning[]
  /** Актуальная версия строки из version_conflict — с ней запрос имеет смысл повторить. */
  readonly currentUpdatedAt: string | null
  readonly changedBy: string | null
  readonly conflicts: readonly BusyConflictInfo[]
  /** Тело целиком: поля, которых ещё нет в этом разборе, не теряются. */
  readonly raw: Readonly<Record<string, unknown>>

  constructor(init: {
    code: ApiErrorCode
    status: number
    title: string
    detail: string | null
    type?: string
    instance?: string | null
    warnings?: readonly CabinetWarning[]
    currentUpdatedAt?: string | null
    changedBy?: string | null
    conflicts?: readonly BusyConflictInfo[]
    raw?: Readonly<Record<string, unknown>>
  }) {
    super(`${init.code}: ${init.title}`)
    this.name = "ApiProblem"
    this.code = init.code
    this.status = init.status
    this.title = init.title
    this.detail = init.detail
    this.type = init.type ?? "about:blank"
    this.instance = init.instance ?? null
    this.warnings = init.warnings ?? []
    this.currentUpdatedAt = init.currentUpdatedAt ?? null
    this.changedBy = init.changedBy ?? null
    this.conflicts = init.conflicts ?? []
    this.raw = init.raw ?? {}
  }

  /**
   * Отказ, после которого повтор того же запроса имеет смысл. Отличать обязательно:
   * повторять «клиент в чёрном списке» бесполезно и вредно, а «сервис не ответил» — нужно.
   */
  get retryable(): boolean {
    if (this.code === "offline" || this.code === "timeout" || this.code === "server_error") {
      return true
    }
    return this.code === "rate_limited" || this.code === "session_refresh_raced"
  }

  /** Сессии больше нет: показывать экран входа, а не отказ в доступе. */
  get sessionLost(): boolean {
    return this.code === "session_invalid" || this.code === "session_reuse_detected"
  }
}

export function isApiProblem(value: unknown): value is ApiProblem {
  return value instanceof ApiProblem
}

/** Ошибка транспорта — там, где тела problem details не было вовсе. */
export function transportProblem(code: TransportErrorCode, status = 0): ApiProblem {
  return new ApiProblem({
    code,
    status,
    title: TRANSPORT_TITLES[code],
    detail: TRANSPORT_DETAILS[code],
  })
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null
}

/**
 * Код отказа из тела ответа.
 *
 * Порядок источников не случаен: поле `code` есть только у доменных отказов записи
 * и у отказов кабинета, а весь контур входа кодирует отказ ссылкой в `type`. Дефисы
 * в ссылке — часть URL; в коде тот же отказ пишется через подчёркивание.
 */
function readCode(body: Record<string, unknown>, status: number): ApiErrorCode {
  const explicit = asString(body["code"])
  if (explicit !== null && explicit.length > 0) return explicit

  const type = asString(body["type"])
  if (type !== null && type.startsWith(PROBLEM_TYPE_PREFIX)) {
    const tail = type.slice(PROBLEM_TYPE_PREFIX.length)
    if (tail.length > 0) return tail.replaceAll("-", "_")
  }

  /*
   * СТАТУС — ПОСЛЕДНЯЯ ОПОРА, И ДЛЯ 429 ОНА ЕДИНСТВЕННАЯ. Лимит nginx срабатывает
   * ДО приложения и отдаёт свою страницу без единого поля problem+json. Пока этот
   * случай падал в "unknown", форма входа читала его как «номер или пароль не подошли»
   * — обвиняла человека в неверном пароле, стирала набранное и предлагала повторить
   * то, что отсекается на подходе. Через 429 не проходит ни одна попытка входа,
   * поэтому он не «unknown» ни при каком теле.
   */
  if (status === 429) return "rate_limited"
  return status >= 500 ? "server_error" : "unknown"
}

function readWarnings(body: Record<string, unknown>): readonly CabinetWarning[] {
  const raw = body["warnings"]
  if (!Array.isArray(raw)) return []
  // Предупреждения показываются словами, поэтому пропускаем только те, у которых есть код.
  return raw.filter((item): item is CabinetWarning => asRecord(item)?.["code"] !== undefined)
}

function readConflicts(body: Record<string, unknown>): readonly BusyConflictInfo[] {
  const raw = body["conflicts"]
  if (!Array.isArray(raw)) return []
  const out: BusyConflictInfo[] = []
  for (const item of raw) {
    const row = asRecord(item)
    if (row === null) continue
    const kind = asString(row["kind"])
    const id = asString(row["id"])
    const startAt = asString(row["startAt"])
    const endAt = asString(row["endAt"])
    if (kind === null || id === null || startAt === null || endAt === null) continue
    if (kind !== "appointment" && kind !== "busy" && kind !== "hold") continue
    out.push({
      kind,
      id,
      startAt,
      endAt,
      employeeId: asString(row["employeeId"]),
      resourceId: asString(row["resourceId"]),
      clientName: asString(row["clientName"]),
      note: asString(row["note"]),
    })
  }
  return out
}

/**
 * Тело ответа → ApiProblem. Тело может быть каким угодно: nginx отдаёт HTML,
 * упавший процесс — пустую строку, поэтому ни одно поле не считается обязательным.
 */
export function problemFromBody(status: number, body: unknown): ApiProblem {
  const row = asRecord(body)
  if (row === null) {
    /*
     * ТЕЛА НЕТ — СТАТУС ВСЁ ЕЩЁ ЗНАЧИТ РОВНО ТО, ЧТО ЗНАЧИТ. Предел nginx срабатывает
     * ДО приложения и отдаёт свою страницу: разбирать нечего, но 429 остаётся «слишком
     * часто». Пока этот случай падал в «unknown», форма входа читала его как «номер или
     * пароль не подошли» — обвиняла человека в неверном пароле, стирала набранное
     * и предлагала повторить то, что отсекается на подходе.
     */
    if (status === 429) {
      return new ApiProblem({
        code: "rate_limited",
        status,
        title: "Слишком много запросов",
        detail:
          "Запросы идут слишком часто. Подождите минуту и повторите — введённое осталось на месте.",
      })
    }
    return transportProblem(status >= 500 ? "server_error" : "unknown", status)
  }

  const code = readCode(row, status)
  const fallbackTitle =
    status >= 500 ? TRANSPORT_TITLES.server_error : TRANSPORT_TITLES.unknown

  return new ApiProblem({
    code,
    status,
    title: asString(row["title"]) ?? asString(row["error"]) ?? fallbackTitle,
    detail: asString(row["detail"]) ?? asString(row["message"]),
    type: asString(row["type"]) ?? "about:blank",
    instance: asString(row["instance"]),
    warnings: readWarnings(row),
    currentUpdatedAt: asString(row["currentUpdatedAt"]),
    changedBy: asString(row["changedBy"]),
    conflicts: readConflicts(row),
    raw: row,
  })
}
